import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Transaction, TransactionStatistics } from "../../types";
import { transactionService } from "../../services/api";

interface TransactionState {
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  statistics: TransactionStatistics | null; // Use the new interface
}

const initialState: TransactionState = {
  transactions: [],
  isLoading: false,
  error: null,
  statistics: null,
};

export const getTransactions = createAsyncThunk(
  "transactions/get",
  async (filters?: {
    type?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    return await transactionService.getTransactions(filters);
  }
);

export const deposit = createAsyncThunk(
  "transactions/deposit",
  async (
    depositData: {
      crypto_amount: number;
      currency: string;
      current_price?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await transactionService.deposit(depositData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to process deposit"
      );
    }
  }
);

export const withdraw = createAsyncThunk(
  "transactions/withdraw",
  async (
    withdrawalData: {
      usd_amount: number;
      currency: string;
      current_price?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      return await transactionService.withdraw(withdrawalData);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Withdrawal failed"
      );
    }
  }
);

export const getTransactionStatistics = createAsyncThunk(
  "transactions/statistics",
  async () => {
    return await transactionService.getStatistics();
  }
);

const transactionSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    clearTransactionError: (state) => {
      state.error = null;
    },
    addTransaction: (state, action: PayloadAction<Transaction>) => {
      state.transactions.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions = action.payload;
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to load transactions";
      })

      .addCase(deposit.fulfilled, (state, action) => {
        state.transactions.unshift(action.payload.transaction);
      })

      .addCase(withdraw.fulfilled, (state, action) => {
        state.transactions.unshift(action.payload.transaction);
      })

      .addCase(getTransactionStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload;
      });
  },
});

export const { clearTransactionError, addTransaction } =
  transactionSlice.actions;
export default transactionSlice.reducer;
